import { createSignal, createEffect, Show, For } from 'solid-js';
import { styled } from 'solid-styled-components';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs';
import { COLORS, SPACING, BORDER_RADIUS } from '../../../constants/design-tokens';
import { APP_CONFIG, ERROR_MESSAGES, A11Y } from '../../../constants/app-constants';
import { useResponsive } from '../../../hooks/useResponsive';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import GlyphControl from '../ui/GlyphControl';

pdfjsLib.GlobalWorkerOptions.workerSrc = String(pdfWorker);

const BASE_SCALE = 1.35;
const MIN_ZOOM = 0.7;
const MAX_ZOOM = 2.2;
const ZOOM_STEP = 0.15;

const PDFContainer = styled.div`
  position: relative;
  box-sizing: border-box;
  flex: 1;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 0;
  height: 100%;
  background: ${COLORS.background.dark};
  overflow-x: ${(props) => (props.allowX ? 'auto' : 'hidden')};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING[4]};
  padding: ${SPACING[4]};
  scrollbar-width: thin;
  scrollbar-color: ${COLORS.phosphor.greenMute} ${COLORS.background.dark};

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${COLORS.background.dark};
  }

  &::-webkit-scrollbar-thumb {
    background: ${COLORS.phosphor.greenMute};
    border-radius: ${BORDER_RADIUS.full};
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${COLORS.phosphor.greenDim};
  }
`;

const PageCanvas = styled.canvas`
  display: block;
  box-sizing: border-box;
  width: auto;
  max-width: ${(props) => (props.zoomed ? 'none' : 'min(840px, 100%)')};
  height: auto;
  background: ${COLORS.background.secondary};
  box-shadow: 0 0 24px ${COLORS.phosphor.bloom};
`;

const ZoomCluster = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING[4]};
`;

const StatusText = styled.p`
  margin: ${SPACING[8]} 0;
  color: ${COLORS.phosphor.greenDim};
  font-size: 0.9rem;
`;

const ErrorBox = styled.div`
  padding: ${SPACING[8]};
  text-align: center;
  color: ${COLORS.text.primary};
`;

/**
 * In-page résumé preview using pdf.js (all viewports).
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {string} props.pdfUrl
 * @param {Function} props.onDownload
 * @param {string} props.fileName
 * @param {boolean} props.showDownloadButton
 */
const PDFPreview = (props) => {
  const {
    isOpen = false,
    onClose,
    pdfUrl,
    onDownload,
    fileName = APP_CONFIG.resumeFileName,
    showDownloadButton = true,
  } = props;

  const { isMobile } = useResponsive();
  const [pageCount, setPageCount] = createSignal(0);
  const [isLoading, setIsLoading] = createSignal(false);
  const [hasError, setHasError] = createSignal(false);
  const [isDownloading, setIsDownloading] = createSignal(false);
  const [zoom, setZoom] = createSignal(1);

  let pdfDoc = null;
  const canvasEls = [];
  const renderTasks = [];

  const paintPage = async (canvas, pageNumber) => {
    if (!canvas || !pdfDoc) {
      return;
    }

    const index = pageNumber - 1;
    if (renderTasks[index]) {
      try {
        renderTasks[index].cancel();
      } catch (_error) {
        /* already finished */
      }
      renderTasks[index] = null;
    }

    const page = await pdfDoc.getPage(pageNumber);
    const viewport = page.getViewport({ scale: BASE_SCALE * zoom() });
    const context = canvas.getContext('2d', { alpha: false });
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const task = page.render({ canvasContext: context, viewport });
    renderTasks[index] = task;

    try {
      await task.promise;
    } catch (error) {
      if (error?.name !== 'RenderingCancelledException') {
        console.error(error);
      }
    } finally {
      if (renderTasks[index] === task) {
        renderTasks[index] = null;
      }
    }
  };

  const shrink = () =>
    setZoom((value) => Math.max(MIN_ZOOM, Math.round((value - ZOOM_STEP) * 100) / 100));
  const enlarge = () =>
    setZoom((value) => Math.min(MAX_ZOOM, Math.round((value + ZOOM_STEP) * 100) / 100));

  const handleDownload = async () => {
    if (isDownloading()) return;
    setIsDownloading(true);
    try {
      if (onDownload) {
        await onDownload();
      } else {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  createEffect(() => {
    if (!isOpen || !pdfUrl) {
      pdfDoc = null;
      setPageCount(0);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setHasError(false);
    setPageCount(0);
    setZoom(1);
    canvasEls.length = 0;
    renderTasks.forEach((task) => {
      try {
        task?.cancel();
      } catch (_error) {
        /* already finished */
      }
    });
    renderTasks.length = 0;

    pdfjsLib
      .getDocument({ url: pdfUrl, withCredentials: false })
      .promise.then((pdf) => {
        if (cancelled) {
          return;
        }
        pdfDoc = pdf;
        setPageCount(pdf.numPages);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(ERROR_MESSAGES.pdfLoadFailed, error);
        if (!cancelled) {
          setIsLoading(false);
          setHasError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  });

  createEffect(() => {
    const count = pageCount();
    zoom();
    if (!pdfDoc || count === 0) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      for (let index = 0; index < count; index += 1) {
        if (canvasEls[index]) {
          paintPage(canvasEls[index], index + 1);
        }
      }
    });

    return () => window.cancelAnimationFrame(frame);
  });

  const modalFooter = () => (
    <Show when={showDownloadButton && !hasError()}>
      <Button
        variant="primary"
        onClick={handleDownload}
        loading={isDownloading()}
        ariaLabel={A11Y.labels.downloadResume}
        data-testid="resume-download-btn"
      >
        download
      </Button>
    </Show>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="résumé"
      headerActions={
        <ZoomCluster>
          <GlyphControl
            mark="-"
            onClick={shrink}
            disabled={zoom() <= MIN_ZOOM}
            ariaLabel="Shrink résumé preview"
            data-testid="resume-zoom-out"
          />
          <GlyphControl
            mark="+"
            onClick={enlarge}
            disabled={zoom() >= MAX_ZOOM}
            ariaLabel="Enlarge résumé preview"
            data-testid="resume-zoom-in"
          />
        </ZoomCluster>
      }
      footer={modalFooter()}
      maxWidth={isMobile() ? '100vw' : '85vw'}
      maxHeight={isMobile() ? '100vh' : '85vh'}
      ariaLabel={A11Y.labels.resumePreview}
      closeOnBackdropClick={!isMobile()}
      data-testid="resume-modal"
    >
      <PDFContainer allowX={zoom() > 1} data-testid="resume-preview">
        <Show when={isLoading()}>
          <StatusText>rendering pages…</StatusText>
        </Show>
        <Show when={hasError()}>
          <ErrorBox>
            <p>{ERROR_MESSAGES.pdfLoadFailed}</p>
            <Button variant="primary" onClick={handleDownload} loading={isDownloading()}>
              download
            </Button>
          </ErrorBox>
        </Show>
        <Show when={!hasError() && pageCount() > 0}>
          <For each={Array.from({ length: pageCount() }, (_, index) => index + 1)}>
            {(pageNumber) => (
              <PageCanvas
                zoomed={zoom() !== 1}
                ref={(el) => {
                  canvasEls[pageNumber - 1] = el || undefined;
                }}
                aria-label={`Resume page ${pageNumber}`}
              />
            )}
          </For>
        </Show>
      </PDFContainer>
    </Modal>
  );
};

export default PDFPreview;
